// Reliability + Audits seed for the retail-chain demo scenario.
// Gives the Issues inbox, Locations catalog, and Audits screens realistic content
// out of the box: a published audit with questions + run history (so Results and
// Statistics populate), four physical store locations, and issues spanning every
// source (overdue, can't-complete flag, manual report, audit finding) plus a
// resolved/dismissed history so "My reports" and the resolved tab aren't empty.
//
// IDs use stable, unique prefixes. References to task IDs match retail-chain.ts.

import {TENANT_ID, WORKSPACE_ID, CLIENT_USER_ID, CLIENT_USER_ID_2} from '../fixtures';
import {TLU_DAILY} from './retail-chain';

// Task IDs referenced by issues (mirror the literals in retail-chain.ts).
const TASK_CASH_DROP = 'ta7001-0000-0000-0000-000000000087';
const TASK_PHOTO_STOCKROOM = 'ta8001-0000-0000-0000-000000000088';
const TASK_ENTRANCE_SIGN = 'tae001-0000-0000-0000-00000000008e';

// ── Locations ─────────────────────────────────────────────────────────────────
export const LOC_DOWNTOWN = 'loc001-0000-0000-0000-0000000000a1';
export const LOC_WESTFIELD = 'loc002-0000-0000-0000-0000000000a2';
export const LOC_HARBOR = 'loc003-0000-0000-0000-0000000000a3';
export const LOC_AIRPORT = 'loc004-0000-0000-0000-0000000000a4';

function loc(id: string, name: string, address: string, createdAt: string) {
    return {
        __typename: 'Location' as const,
        id, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        name, address, archived: false,
        version: 1, createdAt, updatedAt: createdAt,
        createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID, deletedAt: null,
    };
}

export const locations = [
    loc(LOC_DOWNTOWN,  'Downtown Flagship',   '142 Market St, Chicago, IL',   '2026-01-02T09:00:00.000Z'),
    loc(LOC_WESTFIELD, 'Westfield Mall',      '88 Westfield Ave, Oak Park, IL','2026-01-02T09:05:00.000Z'),
    loc(LOC_HARBOR,    'Harbor Point',        '7 Lakeshore Dr, Chicago, IL',   '2026-01-02T09:10:00.000Z'),
    loc(LOC_AIRPORT,   'Airport Terminal 3',  'ORD Terminal 3, Chicago, IL',   '2026-01-02T09:15:00.000Z'),
];

// ── Audit template + questions ──────────────────────────────────────────────────
export const AUD_FLOOR = 'audt01-0000-0000-0000-0000000000b1';

const AQ_ENTRANCE = 'audq01-0000-0000-0000-0000000000c1';
const AQ_SHELVES  = 'audq02-0000-0000-0000-0000000000c2';
const AQ_TICKETS  = 'audq03-0000-0000-0000-0000000000c3';
const AQ_SIGNAGE  = 'audq04-0000-0000-0000-0000000000c4';
const AQ_SAFETY   = 'audq05-0000-0000-0000-0000000000c5';

const AUDIT_NOW = '2026-06-01T10:00:00.000Z';

export const auditTemplates = [
    {
        __typename: 'AuditTemplate' as const,
        id: AUD_FLOOR, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        name: 'Store Floor & Safety Audit',
        description: 'Monthly walk-through scoring floor presentation, pricing accuracy, and safety compliance. Run at each location.',
        requiredPassPercentage: 80,
        publicationState: 'published',
        questionsCount: 5,
        questionsOrder: [AQ_ENTRANCE, AQ_SHELVES, AQ_TICKETS, AQ_SIGNAGE, AQ_SAFETY],
        version: 1, createdAt: AUDIT_NOW, updatedAt: AUDIT_NOW,
        createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID, deletedAt: null,
    },
];

function question(id: string, text: string, points: number, photoRequiredToPass: boolean, reference?: any) {
    return {
        __typename: 'AuditQuestion' as const,
        id, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID, auditTemplateId: AUD_FLOOR,
        text, points, photoRequiredToPass, reference: reference ?? null,
        version: 1, createdAt: AUDIT_NOW, updatedAt: AUDIT_NOW, deletedAt: null,
    };
}

export const auditQuestions = [
    question(AQ_ENTRANCE, 'Is the entrance clean and unobstructed?', 2, false,
        {description: 'Doorway, mats, and the first 3 metres of floor should be clean and free of stock or signage clutter.'}),
    question(AQ_SHELVES, 'Are shelves fully stocked to the face?', 2, false,
        {kbLink: 'lfb001-0000-0000-0000-00000000005b'}),
    question(AQ_TICKETS, 'Are all price tickets visible and correct?', 2, false),
    question(AQ_SIGNAGE, 'Does promotional signage match the current campaign?', 1, false),
    // photoRequiredToPass — the most safety-critical check needs photo evidence to pass.
    question(AQ_SAFETY, 'Are all safety standards met (fire exits clear, wet-floor signs out, first-aid kit stocked)?', 3, true),
];
// Max score: 2 + 2 + 2 + 1 + 3 = 10.

// ── Audit runs (completed history → drives Results + Statistics) ─────────────────
function run(id: string, locationId: string, score: number, completedAt: string, auditorId: string) {
    const maxScore = 10;
    const percentage = Math.round((score / maxScore) * 100);
    return {
        __typename: 'AuditRun' as const,
        id, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID, auditTemplateId: AUD_FLOOR,
        locationId, auditorId,
        status: 'completed' as const,
        score, maxScore, percentage, passed: percentage >= 80,
        startedAt: completedAt, completedAt,
        version: 1, createdAt: completedAt, updatedAt: completedAt, deletedAt: null,
    };
}

const RUN_DOWNTOWN = 'audr01-0000-0000-0000-0000000000d1';
const RUN_WESTFIELD = 'audr02-0000-0000-0000-0000000000d2';
const RUN_HARBOR = 'audr03-0000-0000-0000-0000000000d3'; // failing run → audit_finding issue

export const auditRuns = [
    run(RUN_DOWNTOWN,  LOC_DOWNTOWN,  9, '2026-06-15T11:30:00.000Z', CLIENT_USER_ID),
    run(RUN_WESTFIELD, LOC_WESTFIELD, 8, '2026-06-16T14:00:00.000Z', CLIENT_USER_ID),
    run(RUN_HARBOR,    LOC_HARBOR,    6, '2026-06-18T10:15:00.000Z', CLIENT_USER_ID), // 60% → failed
];

function answer(id: string, runId: string, questionId: string, isYes: boolean, score: number, completedAt: string) {
    return {
        __typename: 'AuditAnswer' as const,
        id, ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        auditRunId: runId, auditQuestionId: questionId,
        answer: isYes, score, photos: null, comment: null,
        createdAt: completedAt, updatedAt: completedAt,
    };
}

export const auditAnswers = [
    // Downtown — 9/10 (fails only Signage)
    answer('ans-dt-1', RUN_DOWNTOWN, AQ_ENTRANCE, true, 2, '2026-06-15T11:30:00.000Z'),
    answer('ans-dt-2', RUN_DOWNTOWN, AQ_SHELVES,  true, 2, '2026-06-15T11:30:00.000Z'),
    answer('ans-dt-3', RUN_DOWNTOWN, AQ_TICKETS,  true, 2, '2026-06-15T11:30:00.000Z'),
    answer('ans-dt-4', RUN_DOWNTOWN, AQ_SIGNAGE,  false, 0, '2026-06-15T11:30:00.000Z'),
    answer('ans-dt-5', RUN_DOWNTOWN, AQ_SAFETY,   true, 3, '2026-06-15T11:30:00.000Z'),
    // Westfield — 8/10 (fails only Tickets)
    answer('ans-wf-1', RUN_WESTFIELD, AQ_ENTRANCE, true, 2, '2026-06-16T14:00:00.000Z'),
    answer('ans-wf-2', RUN_WESTFIELD, AQ_SHELVES,  true, 2, '2026-06-16T14:00:00.000Z'),
    answer('ans-wf-3', RUN_WESTFIELD, AQ_TICKETS,  false, 0, '2026-06-16T14:00:00.000Z'),
    answer('ans-wf-4', RUN_WESTFIELD, AQ_SIGNAGE,  true, 1, '2026-06-16T14:00:00.000Z'),
    answer('ans-wf-5', RUN_WESTFIELD, AQ_SAFETY,   true, 3, '2026-06-16T14:00:00.000Z'),
    // Harbor — 6/10 (fails Shelves + Tickets)
    answer('ans-hb-1', RUN_HARBOR, AQ_ENTRANCE, true, 2, '2026-06-18T10:15:00.000Z'),
    answer('ans-hb-2', RUN_HARBOR, AQ_SHELVES,  false, 0, '2026-06-18T10:15:00.000Z'),
    answer('ans-hb-3', RUN_HARBOR, AQ_TICKETS,  false, 0, '2026-06-18T10:15:00.000Z'),
    answer('ans-hb-4', RUN_HARBOR, AQ_SIGNAGE,  true, 1, '2026-06-18T10:15:00.000Z'),
    answer('ans-hb-5', RUN_HARBOR, AQ_SAFETY,   true, 3, '2026-06-18T10:15:00.000Z'),
];

// ── Issues (inbox) ───────────────────────────────────────────────────────────────
function issue(fields: Partial<Record<string, any>> & {id: string; source: string; title: string}) {
    const created = fields.createdAt ?? '2026-06-20T08:00:00.000Z';
    return {
        __typename: 'Issue' as const,
        ownerId: TENANT_ID, workspaceId: WORKSPACE_ID,
        status: 'open',
        description: null, images: null,
        taskListUnitId: null, locationId: null, taskId: null, taskReportId: null,
        auditRunId: null, sourceRef: null, reportedBy: null,
        overdueCount: 0, lastSignalAt: created,
        resolutionType: null, correctiveTaskId: null, resolutionNote: null, dismissComment: null,
        resolvedBy: null, resolvedAt: null, archivedAt: null,
        version: 1, createdAt: created, updatedAt: created,
        createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID, deletedAt: null,
        ...fields,
    };
}

export const issues = [
    // Overdue — task missed its window, raised by cron (we only seed the row).
    issue({
        id: 'iss001-0000-0000-0000-0000000000e1',
        source: 'overdue', status: 'open',
        title: 'Cash count and safe drop (leave $300 float)',
        description: 'Evening closing task has been missed 3 days running at Downtown Flagship.',
        taskListUnitId: TLU_DAILY, taskId: TASK_CASH_DROP, locationId: LOC_DOWNTOWN,
        overdueCount: 3,
        createdAt: '2026-06-24T23:30:00.000Z', lastSignalAt: '2026-06-26T23:30:00.000Z',
    }),
    // Can't-complete flag — staff marked the task incomplete with a reason.
    issue({
        id: 'iss002-0000-0000-0000-0000000000e2',
        source: 'cant_complete', status: 'open',
        title: 'Photo report: stockroom tidied for next morning',
        description: 'Delivery arrived late and the stockroom still has unshelved cartons — can\'t photograph a tidy room.',
        taskListUnitId: TLU_DAILY, taskId: TASK_PHOTO_STOCKROOM, locationId: LOC_WESTFIELD,
        reportedBy: CLIENT_USER_ID_2,
        createdAt: '2026-06-25T21:10:00.000Z', lastSignalAt: '2026-06-25T21:10:00.000Z',
    }),
    // Manual report — filed by the default persona, shows in "My reports".
    issue({
        id: 'iss003-0000-0000-0000-0000000000e3',
        source: 'manual_report', status: 'open',
        title: 'Broken light fixture above register 2',
        description: 'Flickering and buzzing since this morning. Maintenance needs to look before it fails completely.',
        locationId: LOC_WESTFIELD, reportedBy: CLIENT_USER_ID,
        createdAt: '2026-06-26T09:20:00.000Z', lastSignalAt: '2026-06-26T09:20:00.000Z',
    }),
    // Audit finding — auto-raised by the failing Harbor Point run.
    issue({
        id: 'iss004-0000-0000-0000-0000000000e4',
        source: 'audit_finding', status: 'open',
        title: 'Price tickets missing or incorrect on several shelves',
        description: 'Failed question "Are all price tickets visible and correct?" — several tags didn\'t match the register price during the floor walk.',
        locationId: LOC_HARBOR, auditRunId: RUN_HARBOR,
        sourceRef: {percentage: 60, requiredPassPercentage: 80, score: 6, maxScore: 10},
        createdAt: '2026-06-18T10:15:00.000Z', lastSignalAt: '2026-06-18T10:15:00.000Z',
    }),
    // Resolved with a corrective task — appears in resolved + "My reports".
    issue({
        id: 'iss005-0000-0000-0000-0000000000e5',
        source: 'manual_report', status: 'resolved',
        title: 'Spill near entrance left without a wet-floor sign',
        description: 'Customer nearly slipped. No sign was out.',
        locationId: LOC_DOWNTOWN, reportedBy: CLIENT_USER_ID,
        resolutionType: 'corrective_task', correctiveTaskId: TASK_ENTRANCE_SIGN,
        resolvedBy: CLIENT_USER_ID, resolvedAt: '2026-06-22T12:00:00.000Z',
        createdAt: '2026-06-21T16:40:00.000Z', lastSignalAt: '2026-06-21T16:40:00.000Z',
    }),
    // Dismissed — duplicate, shows the dismissed state + comment.
    issue({
        id: 'iss006-0000-0000-0000-0000000000e6',
        source: 'cant_complete', status: 'dismissed',
        title: 'Could not complete floor walk — manager absent',
        description: 'Opening associate skipped the floor walk.',
        taskListUnitId: TLU_DAILY, locationId: LOC_AIRPORT, reportedBy: CLIENT_USER_ID_2,
        dismissComment: 'Duplicate of the Airport opening checklist follow-up — tracking there instead.',
        resolvedBy: CLIENT_USER_ID, resolvedAt: '2026-06-23T08:30:00.000Z',
        createdAt: '2026-06-23T07:55:00.000Z', lastSignalAt: '2026-06-23T07:55:00.000Z',
    }),
];
